import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  CancelOrderPayload,
  Order,
  OrderListParams,
  OrderListResponse,
  UpdateOrderShippingPayload,
} from './types';

/**
 * Build `/v1/orders` query string. Matches portal's existing param shape so
 * the order-service contract doesn't need to change between the two clients.
 * Exported for unit testing of the param serialization.
 */
export function buildOrderListQuery(params: OrderListParams): string {
  const parts: string[] = [];
  const add = (key: string, value: string | number | boolean | null | undefined): void => {
    if (value === null || value === undefined || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  add('searchAll', params.searchAll);
  add('id', params.id);
  add('externalId', params.externalId);
  add('createdStartDate', params.createdStartDate);
  add('createdEndDate', params.createdEndDate);
  add('shipmentId', params.shipmentId);
  add('shipmentExternalId', params.shipmentExternalId);
  add('shipmentCreatedStartDate', params.shipmentCreatedStartDate);
  add('shipmentCreatedEndDate', params.shipmentCreatedEndDate);
  if (params.isTest) add('isTest', true);
  if (params.isSample) add('isSample', true);
  if (params.isPollingStopped) add('isPollingStopped', true);
  if (params.hasShipmentPendingVendorAcceptance) add('hasShipmentPendingVendorAcceptance', true);
  if (params.hasShipmentPendingMerchantAcceptance) add('hasShipmentPendingMerchantAcceptance', true);
  add('sortBy', params.sortBy);
  if (params.sortDir) add('sortAsc', params.sortDir === 'asc');
  add('page', params.page);
  add('pageSize', params.pageSize);
  for (const status of params.orderStatuses ?? []) add('orderStatuses', status);
  for (const merchantId of params.merchantIds ?? []) add('merchantIds', merchantId);

  return parts.join('&');
}

/**
 * Cache invalidation shared by the three manual-hold mutations. Each changes the
 * order's status and its `manualHold`, both of which the list and the detail view
 * render, so both caches have to drop.
 *
 * SSE also pushes an `orderUpdate` for these, which is what refreshes other open
 * tabs. Invalidating here as well means the acting tab does not sit on a stale
 * button label while waiting for the socket.
 */
const manualHoldInvalidations = (
  _result: unknown,
  _error: unknown,
  orderId: string,
): Array<{ type: 'Order' | 'OrderList'; id: string }> => [
  { type: 'Order', id: orderId },
  { type: 'OrderList', id: 'LIST' },
];

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Order', 'OrderList'],
  endpoints: (builder) => ({
    getOrders: builder.query<OrderListResponse, OrderListParams>({
      query: (params) => ({
        url: `${services.order}/v1/orders?${buildOrderListQuery(params)}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
...result.orders.map((o) => ({ type: 'Order' as const, id: o.id })),
              { type: 'OrderList' as const, id: 'LIST' },
            ]
          : [{ type: 'OrderList' as const, id: 'LIST' }],
    }),

    getOrderDetail: builder.query<Order, string>({
      query: (orderId) => ({
        url: `${services.order}/v1/orders/${orderId}`,
        method: 'GET',
      }),
      transformResponse: (response: { order: Order }) => response.order,
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: orderId }],
    }),

    updateShippingAddress: builder.mutation<
      Order,
      { orderId: string; payload: UpdateOrderShippingPayload }
    >({
      query: ({ orderId, payload }) => ({
        url: `${services.order}/v1/orders/${orderId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order', id: orderId }],
    }),

    cancelOrder: builder.mutation<
      Order,
      {
        orderId: string;
        payload?: CancelOrderPayload & {
          /** When set, only these item lines are canceled (portal's per-item cancel uses this). */
          orderItems?: Array<{ id: string; quantity?: number }>;
          /** Portal sets this to true so the cancel triggers the downstream flow. */
          triggerFlow?: boolean;
        };
      }
    >({
      query: ({ orderId, payload }) => ({
        url: `${services.order}/v1/orders/${orderId}/cancel`,
        method: 'POST',
        body: payload ?? {},
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        { type: 'OrderList', id: 'LIST' },
      ],
    }),

    /**
 * POST /order/v1/orders/{orderId}/hold — body-less.
 *
 * This is **Customer Care Hold**, not the user-placed manual hold. The route
 * name is generic but the order-service handler behind it sets the
 * CustomerCareHold status (confirmed in the dev swagger, which documents this
 * path and no other hold route). It currently has no call site in the portal.
 *
 * If you are wiring the manual hold, use `placeManualHold` below instead —
 * placing a Customer Care Hold would be a different, higher-precedence hold
 * that the user who set it cannot clear.
 */
    holdOrder: builder.mutation<unknown, string>({
      query: (orderId) => ({
        url: `${services.order}/v1/orders/${orderId}/hold`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, orderId) => [
        { type: 'Order', id: orderId },
        { type: 'OrderList', id: 'LIST' },
      ],
    }),

    /**
 * POST /order/v1/orders/{orderId}/manual-hold — place a manual hold.
 * POST /order/v1/orders/{orderId}/manual-hold/extend — Add Time, +10 min.
 * POST /order/v1/orders/{orderId}/manual-hold/resume — release the hold.
 *
 * All three are body-less and return the order carrying its updated
 * `manualHold`. The segment is `manual-hold` rather than `hold` because `/hold`
 * is already taken by Customer Care Hold (see above) and order-service holds a
 * no-breaking-change policy on existing routes.
 *
 * Verified end to end against dev: hold, extend and resume each answered with
 * the order carrying its updated `manualHold`, matching `ManualHold` in
 * `types.ts`. The order swagger still does not list them, so the types here come
 * from those captured responses rather than a generated client.
 */
    placeManualHold: builder.mutation<unknown, string>({
      query: (orderId) => ({
        url: `${services.order}/v1/orders/${orderId}/manual-hold`,
        method: 'POST',
      }),
      invalidatesTags: manualHoldInvalidations,
    }),

    extendManualHold: builder.mutation<unknown, string>({
      query: (orderId) => ({
        url: `${services.order}/v1/orders/${orderId}/manual-hold/extend`,
        method: 'POST',
      }),
      invalidatesTags: manualHoldInvalidations,
    }),

    resumeManualHold: builder.mutation<unknown, string>({
      query: (orderId) => ({
        url: `${services.order}/v1/orders/${orderId}/manual-hold/resume`,
        method: 'POST',
      }),
      invalidatesTags: manualHoldInvalidations,
    }),

    /**
 * POST /order/v1/orders — generic create-order endpoint. The reprint flow
 * posts a payload here with a `reprint: { originalOrderId, reasonId,... }`
 * envelope to clone an existing order. Returns the new orderId.
 */
    createOrder: builder.mutation<{ orderId: string }, Record<string, unknown>>({
      query: (body) => ({
        url: `${services.order}/v1/orders`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'OrderList', id: 'LIST' }],
    }),

    /**
 * PATCH /order/v1/orders/{orderId}/orderItems/{itemId}
 * Used for status='open' (Reopen) and `{ quantity }` (Edit Quantity).
 */
    updateOrderItem: builder.mutation<
      unknown,
      { orderId: string; itemId: string; data: Record<string, unknown> }
    >({
      query: ({ orderId, itemId, data }) => ({
        url: `${services.order}/v1/orders/${orderId}/orderItems/${itemId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Order', id: orderId }],
    }),

    /**
 * PATCH /order/v1/orders/{orderId}/orderitems/{itemId} (lowercase)
 * Used exclusively for image-array replacement (Replace Image modal).
 * Portal has TWO endpoints with different casing:
 * - `/orderItems/` (camelCase): status/quantity updates
 * - `/orderitems/` (lowercase): image-url update
 * Mismatched casing causes 404s on case-sensitive routes, so we mirror
 * portal exactly here — see portal/.../services/orderServices.js:186.
 */
    updateOrderItemImages: builder.mutation<
      unknown,
      { orderId: string; itemId: string; images: unknown[] }
    >({
      query: ({ orderId, itemId, images }) => ({
        url: `${services.order}/v1/orders/${orderId}/orderitems/${itemId}`,
        method: 'PATCH',
        body: { images },
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Order', id: orderId }],
    }),

    /**
 * POST /order/v1/orders/refund body
 * { orderId, refunds: [{ itemId, isShippingRefund, amount, reason }] }
 * Mirrors portal's createRefundRequest. Each entry refunds a specific
 * amount against an item (isShippingRefund=false) or its shipping
 * (isShippingRefund=true). Both can ride on the same orderItemId.
 */
    createRefundRequest: builder.mutation<
      unknown,
      {
        orderId: string;
        refunds: Array<{
          itemId: string;
          isShippingRefund: boolean;
          amount: number;
          reason: string;
        }>;
      }
    >({
      query: (body) => ({
        url: `${services.order}/v1/orders/refund`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Order', id: orderId }],
    }),

    /**
 * POST /order/v1/orders/bulk
 * Body shape: { bulk: [order, order,...] }
 * Each item is a full order spec (mirrors `BulkOrder` from csvParser).
 * Response shape: { bulk: [{ status, response }] } indexed by request order.
 * Used exclusively by the BulkUploadOrders wizard. The submit path
 * batches client-side to keep payloads small and surface partial
 * results — see useBulkOrderUpload for the orchestrator.
 */
    createBulkOrders: builder.mutation<
      { bulk: Array<{ status: number; response?: { orderId?: string; errors?: Array<{ errorMessage: string }> } }> },
      { bulk: unknown[] }
    >({
      query: (body) => ({
        url: `${services.order}/v1/orders/bulk`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'OrderList', id: 'LIST' }],
    }),

    /**
 * PATCH /order/v1/orders/bulk
 * Body shape: { orderId, bulk: [{ id, items: [{ id, status }] }] }
 * Used by UnassignedItemsCard's "Reopen All" button.
 */
    bulkUpdateOrderItems: builder.mutation<
      unknown,
      { orderId: string; bulk: Array<{ id: string; items: Array<{ id: string; status?: string; quantity?: number }> }> }
    >({
      query: ({ orderId, bulk }) => ({
        url: `${services.order}/v1/orders/bulk`,
        method: 'PATCH',
        body: { orderId, bulk },
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrderDetailQuery,
  useUpdateShippingAddressMutation,
  useCancelOrderMutation,
  useHoldOrderMutation,
  usePlaceManualHoldMutation,
  useExtendManualHoldMutation,
  useResumeManualHoldMutation,
  useUpdateOrderItemMutation,
  useUpdateOrderItemImagesMutation,
  useBulkUpdateOrderItemsMutation,
  useCreateBulkOrdersMutation,
  useCreateRefundRequestMutation,
  useCreateOrderMutation,
} = ordersApi;
